/**
 * ServiceAccountTable Component
 *
 * Displays a table of service accounts with actions (view, edit, delete).
 * Includes status badges and role count.
 */

import React from 'react';
import { UserCircleIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Badge } from '../../../components/ui/Badge';
import type { ServiceAccountSanitized, ServiceAccountStatus } from '../../../types/service-account.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface ServiceAccountTableProps {
  serviceAccounts: ServiceAccountSanitized[];
  isLoading: boolean;
  onView: (serviceAccount: ServiceAccountSanitized) => void;
  onEdit?: (serviceAccount: ServiceAccountSanitized) => void;
  onDelete?: (serviceAccount: ServiceAccountSanitized) => void;
  /** Permission to edit service accounts */
  canEdit?: boolean;
  /** Permission to delete service accounts */
  canDelete?: boolean;
}

/**
 * Get status badge styling
 */
function getStatusBadge(status: ServiceAccountStatus): { className: string; label: string } {
  switch (status) {
    case 'active':
      return { className: 'bg-green-100 text-green-800', label: 'Active' };
    case 'inactive':
      return { className: 'bg-gray-100 text-gray-800', label: 'Inactive' };
    case 'suspended':
      return { className: 'bg-red-100 text-red-800', label: 'Suspended' };
    default:
      return { className: 'bg-gray-100 text-gray-800', label: status };
  }
}

export const ServiceAccountTable: React.FC<ServiceAccountTableProps> = ({
  serviceAccounts,
  isLoading,
  onView,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (serviceAccounts.length === 0) {
    return (
      <EmptyState
        icon={<UserCircleIcon className="mx-auto h-12 w-12" />}
        title="No service accounts found"
        description="Create a service account to use for app job execution."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Service Account
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Code
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Roles
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {serviceAccounts.map((sa) => {
            const statusBadge = getStatusBadge(sa.status);
            const appDomainRoleCount = (sa.roles || []).filter(r => r.type === 'APP_DOMAIN').length;

            return (
              <tr key={sa.serviceAccountId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <UserCircleIcon className="h-5 w-5 text-purple-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {sa.serviceAccountName}
                      </div>
                      {sa.description && (
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {sa.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{sa.serviceAccountCode}</code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}
                  >
                    {statusBadge.label}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {(sa.roles || []).length > 0 ? (
                      <>
                        <Badge variant="success">{(sa.roles || []).length} role{(sa.roles || []).length !== 1 ? 's' : ''}</Badge>
                        {appDomainRoleCount > 0 && (
                          <Badge variant="info">
                            {appDomainRoleCount} APP_DOMAIN
                          </Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-gray-400 italic">No roles</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{formatRelativeTime(sa.createdAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onView(sa)}
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                    {canEdit && onEdit && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onEdit(sa)}
                        title="Edit Service Account"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && onDelete && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete(sa)}
                        title="Delete Service Account"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
